<template>
  <div
    class="agent-card"
    :data-server-id="server.server_id"
    @click="openUrl"
    tabindex="0"
    role="button"
    @keydown.enter="openUrl"
    @keydown.space="openUrl"
  >
    <div class="agent-card-header">
      <img src="@/assets/images/chatnav_logo.svg" alt="Agent Logo" class="agent-card-logo">
      <h2 class="agent-card-title">{{ server.name }}</h2>
    </div>
    <div class="agent-card-body">
      <p class="agent-card-status">
        <span class="label">Status:</span>
        <span :class="'status-badge status-' + (server.status || 'unknown')">
          {{ statusLabel(server.status) }}
        </span>
      </p>
      <div v-if="isHubAdmin && server.server_type" class="agent-card-specs">
        <span class="specs-type">{{ server.server_type.toUpperCase() }}</span>
        <span class="specs-detail">{{ serverSpecs(server.server_type) }}</span>
        <span class="specs-price">{{ serverPrice(server.server_type) }}/mo</span>
      </div>
    </div>
    <div class="agent-card-footer">
      <button
        class="demo-btn"
        @click.stop="openDemo"
        :aria-label="'Test ' + server.name + ' agent'"
        tabindex="0"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="5 3 19 12 5 21 5 3"></polygon>
        </svg>
        <span>Demo</span>
        <div class="demo-tooltip">Test {{ server.name }} agent</div>
      </button>
      <button
        class="copy-embed-btn"
        @click.stop="copyEmbedCode"
        aria-label="Copy embed code"
        tabindex="0"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
        <span>Embed</span>
        <div class="embed-tooltip">{{ embedCode }}</div>
      </button>
      <button
        v-if="isHubAdmin"
        class="hetzner-btn"
        @click.stop="syncHetznerType"
        :disabled="hetznerLoading"
        aria-label="Sync server type from Hetzner"
        tabindex="0"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
        <span>{{ hetznerLoading ? '...' : 'Sync' }}</span>
      </button>
      <button
        v-if="canDelete"
        class="delete-btn"
        @click.stop="onDelete"
        aria-label="Delete server"
        tabindex="0"
      >
        <img src="@/assets/images/delete_icon.svg" alt="Delete" class="delete-icon" />
        <div class="delete-tooltip">Delete {{ server.name }}</div>
      </button>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/services/api';
export default {
  name: 'AgentCard',
  props: {
    server: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: String,
      default: ''
    },
    currentUsername: {
      type: String,
      default: ''
    },
    isHubAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hetznerLoading: false,
    };
  },
  computed: {
    canDelete() {
      // miro@chatnav.net can delete any server
      if (this.currentUsername === 'miro@chatnav.net') {
        return true;
      }
      // Users can only delete servers they own
      return this.server.owner_id === this.currentUserId;
    },
    embedCode() {
      const subdomain = this.server.subdomain || '';
      const domain = this.server.domain || 'chatnav.ai';
      return '<script src="https://' + subdomain + '.' + domain + '/static/chatbot/embed.js" id="chatnav-bot" async><' + '/script>';
    }
  },
  methods: {
    openUrl() {
      window.open(this.server.dashboard_url, '_blank');
    },
    statusLabel(status) {
      switch ((status || '').toLowerCase()) {
        case 'creating': return 'Creating';
        case 'active': return 'Active';
        case 'inactive': return 'Inactive';
        case 'trial': return 'Trial';
        case 'error': return 'Error';
        case 'health_check_failed': return 'Health Check Failed';
        default: return 'Unknown';
      }
    },
    serverSpecs(type) {
      const specs = {
        cx23: '2 vCPU · 4 GB RAM · 40 GB SSD',
        cx33: '4 vCPU · 8 GB RAM · 80 GB SSD',
        cx43: '8 vCPU · 16 GB RAM · 160 GB SSD',
        cx53: '16 vCPU · 32 GB RAM · 320 GB SSD',
        cpx11: '2 vCPU AMD · 2 GB RAM · 40 GB SSD',
        cpx21: '3 vCPU AMD · 4 GB RAM · 80 GB SSD',
        cpx31: '4 vCPU AMD · 8 GB RAM · 160 GB SSD',
        cpx41: '8 vCPU AMD · 16 GB RAM · 240 GB SSD',
        cpx51: '16 vCPU AMD · 32 GB RAM · 360 GB SSD',
      };
      return specs[(type || '').toLowerCase()] || type;
    },
    serverPrice(type) {
      const prices = {
        // CX = Cost-Optimized; CPX = Regular Performance. All + €0.50/mo primary IPv4.
        cx23: '€4.99', cx33: '€7.49', cx43: '€12.99', cx53: '€23.49',
        cpx11: '€6.49', cpx21: '€10.49', cpx31: '€18.49', cpx41: '€33.49', cpx51: '€71.99',
      };
      return prices[(type || '').toLowerCase()] || '';
    },
    async syncHetznerType() {
      this.hetznerLoading = true;
      try {
        const response = await apiFetch(`/api/hetzner-server-info?server_id=${this.server.server_id}`);
        if (response.ok) {
          window.location.reload();
        }
      } finally {
        this.hetznerLoading = false;
      }
    },
    onDelete() {
      this.$emit('delete', this.server.server_id);
    },
    openDemo() {
      sessionStorage.setItem('chatnav_demo_name', this.server.name);
      window.open('/demo/' + this.server.subdomain, '_blank');
    },
    async copyEmbedCode() {
      try {
        await navigator.clipboard.writeText(this.embedCode);
        this.$emit('copy-success', this.server.name);
      } catch (err) {
        console.error('Failed to copy embed code:', err);
        this.$emit('copy-error');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/agent-card.scss";
</style>